export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export type InstallPromptListener = (event: BeforeInstallPromptEvent | null) => void;

let capturedInstallPrompt: BeforeInstallPromptEvent | null = null;
const installPromptListeners = new Set<InstallPromptListener>();

function notifyInstallPromptListeners() {
  for (const listener of installPromptListeners) {
    listener(capturedInstallPrompt);
  }
}

export function getCapturedInstallPrompt() {
  return capturedInstallPrompt;
}

export function subscribeToInstallPrompt(listener: InstallPromptListener) {
  installPromptListeners.add(listener);
  return () => {
    installPromptListeners.delete(listener);
  };
}

export function consumeInstallPrompt() {
  capturedInstallPrompt = null;
  notifyInstallPromptListeners();
}

// Test-only: this module's capture state is intentionally at module scope (see below), so it
// persists across tests within the same test file unless explicitly reset. Not used by app code.
// Deliberately does NOT clear installPromptListeners: that set also holds listeners registered
// once at other modules' own top-level scope (e.g. pwaInstallVisibility.ts's re-arm subscription),
// which only ever run their module-scope registration once per test file, not once per test --
// clearing the set here would silently and permanently remove them after the first test.
export function __resetInstallPromptCaptureForTests() {
  capturedInstallPrompt = null;
}

// Captured at module scope -- not inside a React effect -- and with preventDefault() called
// unconditionally and synchronously. Browsers can fire beforeinstallprompt as soon as their own
// installability heuristics are met, which can happen before this app has finished mounting
// PwaStatusPrompts (itself gated behind an async runtime-config fetch), especially on slower
// mobile/tablet devices and networks. A useEffect-based listener only attaches after that whole
// chain resolves, so on slower devices the browser's own mini-infobar/install UI won the race and
// appeared instead of MedoPilot's own card. Listening here, at first module evaluation (this
// module is imported at the top of PwaStatusPrompts.tsx, itself imported at the top of App.tsx,
// itself imported before ReactDOM even renders), closes that gap: the event is intercepted and
// its default UI suppressed as early as this app's JS can run, regardless of when -- or whether
// -- a component later mounts to consume it.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    capturedInstallPrompt = event as BeforeInstallPromptEvent;
    notifyInstallPromptListeners();
  });

  window.addEventListener("appinstalled", () => {
    consumeInstallPrompt();
  });
}
